/** Title / tags / body form, shared by create and edit. */

import type { NoteInput } from "../api/types";
import { describeError } from "../lib/errors";
import { el } from "../lib/dom";
import { BODY_FOCUS_EVENT } from "../lib/richEditor";
import { parseTagInput } from "../lib/tagInput";
import { folderSelect } from "./folderSelect";
import type { FolderSelectHandle } from "./folderSelect";

export interface NoteFormInitial {
  readonly title: string;
  readonly body: string;
  readonly tags: readonly string[];
  readonly folder: string;
}

export interface NoteFormOptions {
  readonly initial: NoteFormInitial;
  /**
   * Existing folders for the picker. When absent the picker is not rendered
   * and the note's folder is left alone — the form then sends no `folder` at
   * all, which the server's PUT treats as "unchanged".
   */
  readonly folders?: readonly string[];
  /** Overrides the sentence under the folder picker; see `folderSelect`. */
  readonly folderHint?: string;
  readonly submitLabel: string;
  readonly cancelHref: string;
  readonly onSubmit: (input: NoteInput) => Promise<void>;
  readonly onDelete?: () => Promise<void>;
  /**
   * Supplies a title when the field is left blank, given the chosen folder.
   *
   * Absent means a title is required, which is the right behaviour for edit
   * mode: silently replacing a title the user cleared on an existing note
   * would look like data loss. Create mode passes one so a note can be
   * captured without stopping to name it.
   */
  readonly titleFallback?: (folder: string) => string;
  /**
   * Called once, with the form built but not yet in the document.
   *
   * Exists so a page can mount the rich editor over the body field without
   * this component knowing the editor exists. The form keeps returning a plain
   * element, so `runAsyncView`'s `render: (value) => Node` contract is unchanged.
   */
  readonly onMount?: (handle: NoteFormHandle) => void;
}

export interface NoteFormHandle {
  /**
   * The body field. Monaco hides it and writes through to its `value`, which
   * is why the submit handler below needs no knowledge of any of that.
   */
  readonly body: HTMLTextAreaElement;
  /** Submits the form as the button would, validation included. */
  readonly requestSubmit: () => void;
}

function labelledField(label: string, hint: string, control: HTMLElement, area: string): HTMLElement {
  const id = `field-${area}`;
  control.id = id;
  return el("div", {
    className: "field",
    dataset: { area },
    children: [
      el("label", { className: "field__label", attrs: { for: id }, text: label }),
      control,
      el("p", { className: "field__hint", text: hint }),
    ],
  });
}

export function noteForm(options: NoteFormOptions): HTMLElement {
  // `required` would make the browser block submission before the fallback
  // ever runs, so it is only set when there is no fallback to fall back to.
  const titleOptional = options.titleFallback !== undefined;
  const titleInput = el("input", {
    className: "input",
    attrs: {
      type: "text",
      name: "title",
      placeholder: titleOptional ? "Note title (optional)" : "Note title",
      autocomplete: "off",
      ...(titleOptional ? {} : { required: true }),
    },
  });
  titleInput.value = options.initial.title;

  const tagsInput = el("input", {
    className: "input",
    attrs: { type: "text", name: "tags", placeholder: "comma separated", autocomplete: "off" },
  });
  tagsInput.value = options.initial.tags.join(", ");

  const bodyInput = el("textarea", {
    className: "input input--body",
    attrs: { name: "body", rows: 24, spellcheck: true, placeholder: "# Markdown goes here" },
  });
  bodyInput.value = options.initial.body;

  const folder: FolderSelectHandle | null =
    options.folders === undefined
      ? null
      : folderSelect({
          folders: options.folders,
          initial: options.initial.folder,
          ...(options.folderHint === undefined ? {} : { hint: options.folderHint }),
        });

  const status = el("p", { className: "form__status", attrs: { role: "status", "aria-live": "polite" } });

  const submitButton = el("button", {
    className: "button button--primary",
    attrs: { type: "submit" },
    text: options.submitLabel,
  });

  const deleteButton =
    options.onDelete === undefined
      ? null
      : el("button", { className: "button button--danger", attrs: { type: "button" }, text: "Delete" });

  const setBusy = (busy: boolean): void => {
    submitButton.disabled = busy;
    if (deleteButton !== null) {
      deleteButton.disabled = busy;
    }
  };

  const form = el("form", {
    className: "note-form",
    children: [
      labelledField("Title", "Shown in lists and search results.", titleInput, "title"),
      labelledField("Tags", "Comma separated; used by the tag cloud.", tagsInput, "tags"),
      folder?.element ?? null,
      labelledField("Body", "Markdown. Rendered on the note page.", bodyInput, "body"),
      el("div", {
        className: "form__actions",
        dataset: { area: "actions" },
        children: [
          deleteButton,
          status,
          el("a", { className: "button button--secondary", attrs: { href: options.cancelHref }, text: "Cancel" }),
          submitButton,
        ],
      }),
    ],
  });

  form.addEventListener("submit", (event: SubmitEvent) => {
    event.preventDefault();

    const folderValue = folder === null ? options.initial.folder : folder.getValue();

    let title = titleInput.value.trim();
    if (title === "") {
      const fallback = options.titleFallback;
      if (fallback === undefined) {
        status.textContent = "A title is required.";
        status.classList.add("form__status--error");
        titleInput.focus();
        return;
      }
      title = fallback(folderValue);
    }

    const input: NoteInput = {
      title,
      body: bodyInput.value,
      tags: parseTagInput(tagsInput.value),
      ...(folder === null ? {} : { folder: folderValue }),
    };

    status.classList.remove("form__status--error");
    status.textContent = "Saving…";
    setBusy(true);

    void (async (): Promise<void> => {
      try {
        await options.onSubmit(input);
      } catch (error: unknown) {
        status.textContent = describeError(error);
        status.classList.add("form__status--error");
        setBusy(false);
      }
    })();
  });

  if (deleteButton !== null && options.onDelete !== undefined) {
    const onDelete = options.onDelete;
    deleteButton.addEventListener("click", () => {
      if (!window.confirm("Delete this note? The sync daemon will remove the local file too.")) {
        return;
      }
      status.classList.remove("form__status--error");
      status.textContent = "Deleting…";
      setBusy(true);
      void (async (): Promise<void> => {
        try {
          await onDelete();
        } catch (error: unknown) {
          status.textContent = describeError(error);
          status.classList.add("form__status--error");
          setBusy(false);
        }
      })();
    });
  }

  // Cmd/Ctrl-1 title, 2 folder, 3 body, 4 tags, and Cmd-E (body) jump between fields; Cmd-S saves from any of them.
  // Registered on the document in the capture phase so it works with nothing
  // focused, and so it beats Monaco's own Cmd-E ("find with selection"). Monaco
  // keeps Cmd-S for itself (its command calls the same requestSubmit), so a press
  // inside it is left alone rather than saving twice.
  const focusBody = (): void => {
    // Focusing Monaco's hidden input from outside leaves the field "selected"
    // with no caret drawn; only the editor's own focus() shows one. The rich
    // editor listens for this event (lib/richEditor.ts) and calls it.
    if (form.querySelector(".editor-monaco") !== null) {
      bodyInput.dispatchEvent(new Event(BODY_FOCUS_EVENT));
    } else {
      bodyInput.focus();
    }
  };
  let wasConnected = false;
  const onShortcut = (event: KeyboardEvent): void => {
    if (!form.isConnected) {
      // Not mounted yet, or navigated away: unbind only in the latter case.
      if (wasConnected) {
        document.removeEventListener("keydown", onShortcut, true);
      }
      return;
    }
    wasConnected = true;
    if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey || event.isComposing) {
      return;
    }
    const key = event.key.toLowerCase();
    const inMonaco = event.target instanceof Element && event.target.closest(".editor-monaco") !== null;
    let action: (() => void) | null = null;
    switch (key) {
      case "s":
        action = inMonaco ? null : (): void => form.requestSubmit();
        break;
      case "1":
        action = (): void => titleInput.focus();
        break;
      case "2":
        action = folder === null ? null : (): void => folder.open();
        break;
      case "3":
      case "e":
        action = focusBody;
        break;
      case "4":
        action = (): void => tagsInput.focus();
        break;
    }
    if (action === null) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    action();
  };
  document.addEventListener("keydown", onShortcut, true);

  options.onMount?.({
    body: bodyInput,
    requestSubmit: (): void => form.requestSubmit(),
  });

  return form;
}
